import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from '@untitledui/icons';
import { LinkProps } from 'config/links';

import s from './sidelink.module.scss';

/** Учитывает `?name=` у виджетов: pathname один `/widget`, активна конкретная витрина. */
export const isPathActive = (pathname: string, search: string, path: string) => {
  const [pathOnly, query = ''] = path.split('?');
  if (!pathname.includes(pathOnly)) return false;
  if (!query) return true;
  const params = new URLSearchParams(search);
  const expected = new URLSearchParams(query);
  return Array.from(expected.entries()).every(([key, value]) => params.get(key) === value);
};

/**
 * Пункт без подменю ведёт на одну вкладку раздела (`/powers-attorney/active`,
 * `/counterparties/all-counterparties`), а пользователь бывает и на соседних: «Отозванные»,
 * «Приглашения», «Поиск и приглашение». Такой пункт активен во всём разделе — по первому
 * сегменту пути. Витрины с `?name=` и пункты с подменю — по точному адресу, как раньше.
 */
export const isSectionActive = (pathname: string, path: string) => {
  if (path.includes('?')) return false;
  const [section] = path.split('/').filter(Boolean);
  return Boolean(section) && (pathname === `/${section}` || pathname.startsWith(`/${section}/`));
};

export const SideLink = ({ link }: { link: LinkProps }) => {
  const { label, path, icon, subLinks = [] } = link;
  const { pathname, search } = useLocation();
  const { t } = useTranslation();

  const hasSubLinks = subLinks.length > 0;
  const isActive =
    isPathActive(pathname, search, path) ||
    (!hasSubLinks && isSectionActive(pathname, path)) ||
    subLinks.some((sub) => isPathActive(pathname, search, sub.path));
  const [isOpen, setIsOpen] = useState(isActive);

  // Раздел раскрывается сам, когда пользователь попал в него по ссылке
  useEffect(() => {
    if (isActive) setIsOpen(true);
  }, [isActive]);

  const content = (
    <>
      {icon && <span className={s.icon}>{icon}</span>}
      <span className={s.label}>{t(label)}</span>
      {hasSubLinks && <ChevronDown className={cx(s.chevron, { [s.chevronOpen]: isOpen })} />}
    </>
  );

  return (
    <li>
      {hasSubLinks ? (
        <button
          type="button"
          className={cx(s.item, { [s.active]: isActive })}
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          {content}
        </button>
      ) : (
        <Link to={path} className={cx(s.item, { [s.active]: isActive })}>
          {content}
        </Link>
      )}

      {hasSubLinks && isOpen && (
        <ul className={s.subMenu}>
          {subLinks.map((sub) => (
            <li key={sub.label}>
              <Link
                to={sub.path}
                className={cx(s.subLink, {
                  [s.subLinkActive]: isPathActive(pathname, search, sub.path),
                })}
              >
                {t(sub.label)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};
